import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, setWorkspaceCurrency } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Surface, Btn, Field, Input, Select, CurrencyInput } from "../components/Primitives";
import { CheckCircle, WarningCircle, Warning, Lock } from "@phosphor-icons/react";

const RESET_PHRASE = "RESET DATABASE PERMANENTLY";

export default function Settings() {
  const { user, tenant, refreshTenant } = useAuth();
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const [senderCheck, setSenderCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [currency, setCurrency] = useState("EUR");
  const [currencySaving, setCurrencySaving] = useState(false);
  const [currencySaved, setCurrencySaved] = useState(false);

  useEffect(() => { api.get("/app-settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { if (tenant?.currency) setCurrency(tenant.currency); }, [tenant?.currency]);

  const saveCurrency = async () => {
    setCurrencySaving(true);
    setCurrencySaved(false);
    try {
      await api.patch("/tenants/me", { currency });
      setWorkspaceCurrency(currency);
      await refreshTenant();
      setCurrencySaved(true);
      setTimeout(() => setCurrencySaved(false), 2500);
    } finally {
      setCurrencySaving(false);
    }
  };

  const save = async () => {
    setSaved(false);
    await api.patch("/app-settings", {
      avgas_per_liter: parseFloat(s.avgas_per_liter || 0),
      mogas_per_liter: parseFloat(s.mogas_per_liter || 0),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const verifySender = useCallback(async () => {
    setChecking(true);
    try {
      const r = await api.post("/email/verify-sender");
      setSenderCheck(r.data);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { if (s?.email?.resend_configured) verifySender(); }, [s, verifySender]);

  if (!s) return null;
  const email = s.email || {};
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";

  return (
    <div>
      <PageHeader overline="Admin" title="Settings"
        subtitle="Group-wide configuration. Fuel rates apply to the calculated fuel cost on flight logs." />
      <div className="p-8 space-y-6">
        <Surface className="p-6 max-w-2xl" data-testid="workspace-currency-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Workspace currency</div>
          <div className="text-sm text-zinc-400 mb-4">
            Single currency for every aircraft, reserve, settlement, expense and contribution
            in this workspace. Change it once — all dashboards, forms and labels follow.
            {!isOwner && <span className="text-zinc-600"> Read-only — only the workspace owner can change this.</span>}
          </div>
          <div className="flex items-end gap-4">
            <Field label="Currency">
              <Select disabled={!isOwner} value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="workspace-currency-select">
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
              </Select>
            </Field>
            {isOwner && (
              <Btn onClick={saveCurrency} disabled={currencySaving || currency === tenant?.currency} data-testid="workspace-currency-save">
                {currencySaving ? "Saving…" : currencySaved ? "Saved ✓" : "Save"}
              </Btn>
            )}
          </div>
        </Surface>

        <Surface className="p-6 max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Fuel rates ({tenant?.currency || "EUR"} per litre)</div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="AVGAS 100LL"><CurrencyInput data-testid="setting-avgas" value={s.avgas_per_liter} onChange={(v) => setS({ ...s, avgas_per_liter: v })} /></Field>
            <Field label="MOGAS"><CurrencyInput data-testid="setting-mogas" value={s.mogas_per_liter} onChange={(v) => setS({ ...s, mogas_per_liter: v })} /></Field>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <div className="text-xs text-zinc-500">Each aircraft uses its <code className="text-amber">fuel_type</code> to pick the correct rate. Containing &quot;MOGAS&quot; → MOGAS, otherwise AVGAS.</div>
            <Btn onClick={save} data-testid="settings-save-btn">{saved ? "Saved" : "Save"}</Btn>
          </div>
        </Surface>

        <Surface className="p-6 max-w-2xl" data-testid="email-config-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Outbound email (Resend)</div>
          <div className="text-sm text-zinc-300 mb-4">
            Sender: <span className="font-mono text-amber">{email.sender || "(not configured)"}</span>
          </div>
          {!email.resend_configured && (
            <div className="p-3 border border-red-900/60 bg-red-950/20 text-sm text-red-300 font-mono" data-testid="resend-not-configured">
              RESEND_API_KEY missing — invite and approval emails are skipped. Set it in <code>backend/.env</code>.
            </div>
          )}
          {email.resend_configured && senderCheck && (
            senderCheck.verified ? (
              <div className="p-3 border border-green-900/60 bg-green-950/20 text-sm text-green-300 flex items-center gap-2" data-testid="resend-verified">
                <CheckCircle size={16} weight="fill" />
                Domain <code className="text-amber font-mono">{senderCheck.domain}</code> is verified. Outbound emails will deliver normally.
              </div>
            ) : (
              <div className="p-3 border border-amber-900/60 bg-amber-950/20 text-sm text-amber-200" data-testid="resend-unverified">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <WarningCircle size={16} weight="fill" />
                  Domain not verified — emails currently deliver only to the Resend account owner.
                </div>
                <div className="text-xs opacity-80 leading-relaxed">
                  Reason: {senderCheck.reason || senderCheck.status || "—"}. Add <code className="text-amber">{senderCheck.domain}</code> at <a className="underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>, add the DKIM + SPF DNS records at your registrar, then click Verify on Resend. Re-check below when done.
                </div>
              </div>
            )
          )}
          <div className="mt-4">
            <Btn variant="ghost" onClick={verifySender} disabled={checking} data-testid="verify-sender-btn">
              {checking ? "Checking…" : "Re-check verification"}
            </Btn>
          </div>
        </Surface>

        {isOwner && <DangerZone />}
        {user?.password_set_at && <ChangePasswordCard />}
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const canSubmit =
    current.length >= 1 &&
    next.length >= 10 &&
    next === confirm &&
    /[A-Za-z]/.test(next) &&
    /\d/.test(next) &&
    !busy;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null); setBusy(true);
    try {
      await api.post("/auth/password/change", {
        current_password: current,
        new_password: next,
      });
      setMsg("Password updated. All other sessions have been signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="p-6 max-w-2xl" data-testid="change-password-card">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4 flex items-center gap-2">
        <Lock size={12} /> Change your password
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Current password">
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            data-testid="pw-current"
          />
        </Field>
        <Field label="New password"
               hint="Minimum 10 characters, at least one letter and one digit.">
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            data-testid="pw-new"
          />
        </Field>
        <Field label="Confirm new password">
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid="pw-confirm"
          />
          {confirm && confirm !== next && (
            <div className="text-[11px] text-red-400 mt-1 font-mono">
              Passwords do not match.
            </div>
          )}
        </Field>
        {msg && (
          <div className="p-3 border border-green-900/60 bg-green-950/20 text-xs text-green-300"
               data-testid="pw-change-success">
            {msg}
          </div>
        )}
        {err && (
          <div className="p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300"
               data-testid="pw-change-error">
            {err}
          </div>
        )}
        <div className="pt-2">
          <Btn
            type="submit"
            disabled={!canSubmit}
            data-testid="pw-change-submit"
            className="disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : "Change password"}
          </Btn>
        </div>
      </form>
    </Surface>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  return (
    <Surface
      className="p-6 max-w-2xl border-red-900/60"
      data-testid="danger-zone-card"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-red-400 font-mono mb-2 flex items-center gap-2">
        <Warning size={14} weight="fill" /> Danger zone
      </div>
      <div className="text-sm text-zinc-300 mb-4 leading-relaxed">
        Permanently delete <span className="text-amber font-mono">every record</span> in this installation —
        participants, aircraft, memberships, bookings, flight logs, settlements,
        expenses, contributions, documents, and the audit trail.
        You will be logged out, and the next person to sign in becomes the new
        Owner of a blank instance.
      </div>
      <div className="text-[11px] text-zinc-500 mb-4 font-mono">
        This action is <span className="text-red-400">IRREVERSIBLE</span>. There is no undo, and no backup is taken.
      </div>
      <Btn
        variant="danger"
        onClick={() => setOpen(true)}
        data-testid="open-reset-db-btn"
      >
        Reset entire database
      </Btn>
      {open && <ResetDatabaseDialog onClose={() => setOpen(false)} />}
    </Surface>
  );
}

function ResetDatabaseDialog({ onClose }) {
  const { user } = useAuth();
  const [phrase, setPhrase] = useState("");
  const [password, setPassword] = useState("");
  const [ack, setAck] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  // 5-second cooldown before the final button becomes enabled — prevents
  // reflex clicking even after a correctly typed confirmation phrase.
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Owner has a password set iff they ever signed in via email/password. We
  // don't have direct state for it here, so we require password whenever the
  // user entry exposes a `password_set_at` indicator. Otherwise the Google-
  // only owner only needs the phrase + acknowledgement + cooldown.
  const needsPassword = Boolean(user?.password_set_at);
  const phraseOk = phrase === RESET_PHRASE;
  const passwordOk = !needsPassword || password.length >= 1;
  const canSubmit = phraseOk && ack && passwordOk && countdown === 0 && !submitting;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/admin/reset-database", {
        confirm_phrase: phrase,
        i_understand: true,
        ...(needsPassword ? { password } : {}),
      });
      // Session is invalidated server-side; hard-redirect to login.
      window.location.href = "/login?reset=1";
    } catch (e) {
      setError(e.response?.data?.detail || "Reset failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="reset-db-dialog"
    >
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-red-900/60 shadow-[0_0_40px_rgba(239,68,68,0.15)]">
        <div className="p-6 border-b border-zinc-900 flex items-center gap-3">
          <Warning size={22} weight="fill" className="text-red-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-red-400 font-mono">
              Irreversible action
            </div>
            <div className="text-lg text-zinc-100">Reset entire database</div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-200 leading-relaxed">
            This will wipe every collection in MongoDB, including the audit log
            and every user account. You will be signed out. The first person to
            sign in afterwards becomes the new Owner.
          </div>

          <Field
            label={`Type exactly: ${RESET_PHRASE}`}
            hint="Case-sensitive, no surrounding whitespace."
          >
            <Input
              autoFocus
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={RESET_PHRASE}
              data-testid="reset-db-phrase-input"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>

          {needsPassword && (
            <Field label="Confirm with your current password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                data-testid="reset-db-password-input"
                autoComplete="current-password"
              />
            </Field>
          )}

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-1 accent-red-500 w-4 h-4"
              data-testid="reset-db-ack-checkbox"
            />
            <span className="text-xs text-zinc-300 leading-relaxed">
              I understand this erases every participant, aircraft, flight log,
              settlement, expense and document — permanently, with no backup.
            </span>
          </label>

          {error && (
            <div
              className="p-3 border border-red-900/60 bg-red-950/30 text-xs text-red-300 font-mono"
              data-testid="reset-db-error"
            >
              {error}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-zinc-900 flex items-center justify-between gap-3">
          <Btn
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="reset-db-cancel-btn"
          >
            Cancel
          </Btn>
          <Btn
            variant="danger"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="reset-db-confirm-btn"
            className="disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Resetting…"
              : countdown > 0
                ? `Confirm in ${countdown}s`
                : "Reset everything"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
